import { COLORS, type ColorId } from '../data/colors'
import { CATEGORIES } from '../data/categories'
import type { ColorfulPlayer, ColorfulState } from './types'

/** Fisher–Yates, returns a NEW array. */
function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/**
 * Build an initial state per the rules:
 *   - All 15 colors laid out; randomly discard 5.
 *   - 10 remaining become the shared hand everyone will draw from.
 *   - Shuffle the category deck.
 *   - Round count: 5, or 6 if 6 players.
 *   - Picker index: random (stands in for "youngest player"), advances clockwise each round.
 */
export function createInitialState(players: ColorfulPlayer[]): ColorfulState {
  if (players.length < 2 || players.length > 6) {
    throw new Error('Colorful needs 2–6 players')
  }

  const shuffledColors = shuffle(COLORS.map(c => c.id))
  const discarded = shuffledColors.slice(0, 5)
  const available = shuffledColors.slice(5)

  const maxRounds = players.length === 6 ? 6 : 5
  const picker = Math.floor(Math.random() * players.length)

  return {
    players,
    maxRounds,
    discardedColors: discarded as ColorId[],
    availableColors: available as ColorId[],
    deck: shuffle(CATEGORIES),
    rounds: [],
    currentRound: 0,
    picker,
    pickingIndex: 0,
    phase: 'setup',
    pendingCard: null,
    redrew: false,
  }
}

/**
 * Colors a given player has NOT yet used across the match. Each color
 * can only be played once, so we subtract their historical picks.
 */
export function remainingColorsFor(state: ColorfulState, playerId: string): ColorId[] {
  const used = new Set<ColorId>()
  for (const r of state.rounds) {
    const pick = r.picks[playerId]
    if (pick) used.add(pick)
  }
  return state.availableColors.filter(c => !used.has(c))
}

/** Round N (1-indexed) → which prompt slot on the card to use. */
export function slotForRound(roundIndex0: number): 0 | 1 | 2 | 3 {
  // Rounds 1-3 use slots 1-3; rounds 4 AND 5 (and 6, with 6 players)
  // both use slot 4-5.
  if (roundIndex0 === 0) return 0
  if (roundIndex0 === 1) return 1
  if (roundIndex0 === 2) return 2
  return 3
}
